import { observer } from "mobx-react";
import { DefaultModalLayout } from "../../../../../components/modal/layouts/default-modal-layout";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import { FONT_COLOR } from "../../../../../components/fonts/font-props";
import './rma-action-modal.scss';
import { FormField } from "../../../../../components/form-field/form-field";
import { Checkbox } from "../../../../../components/inputs/checkbox/checkbox";
import { EDITOR_VARIANT, RichTextEditor } from "../../../../../components/rich-text-editor/rich-text-editor";
import ListLayout from "../../../../../components/layouts/list-layout/list-layout";
import FileUpload from "../../../../../components/file-upload/file-upload";
import { IOption, SelectDropdown } from "../../../../../components/inputs/select-dropdown/select-dropdown";
import { BUTTON_VARIANTS } from "../../../../../components/inputs/button-variants";
import { Button } from "../../../../../components/inputs/button/button";
import RmaPageStore, { IRmaAction, RMA_ACTION_TYPE } from "../../stores/rma-page-store";
import { SingleValue } from "react-select";
import RmaEmailAttachments from "./rma-email-attachments/rma-email-attachments";
import { NOTE_TYPE, Note } from "../../../../../components/note/note";
import { Input } from "../../../../../components/inputs/input/input";
import RmaActionChangeReturnMethodConfirm from "../rma-action-change-return-method-confirm/rma-action-change-return-method-confirm";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import useTranslation from "../../../../../translation/hooks/translation-hook";
import TranslationScope from "../../../../../translation/translation-scope";
import { useEffect } from "react";

interface IProps {
    rma: RmaPageStore;
    rmaAction: IRmaAction;
    onCloseClick: () => void;
    isDisabled?: boolean;
}

const RmaActionModalContent: React.FC<IProps> = observer(({ rma, rmaAction, onCloseClick, isDisabled}) => {
    const actionStore = rmaAction.store;    
    
    const shouldAllowReturnMethodSelect = rmaAction.type === RMA_ACTION_TYPE.APPROVE;

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';
    const approveRequestScope = 'dashboard-approve-request-popup';

    const onSubmit = () => {
        actionStore.submitAction(onCloseClick);
    };

    const onUploadFile = (fileData: File, fileContent: string | ArrayBuffer | null | undefined) => {
        actionStore.addEmailAttachment({
            name: fileData.name,
            type: fileData.type,
            base64: (fileContent as string).split(',')[1],
        });        
    };

    const selectedReturnMethod = rma.returnMethodOptions.find(o => o.value === actionStore.newReturnMethodId);

    const onReturnMethodChange = (v: SingleValue<IOption>) => {        
        actionStore.setNewReturnMethodId(v?.value as number);
    }

    return (
        <>
            <DefaultModalLayout isCloseDisabled={true} onCloseClicked={() => { }}>
                <div className={'action-modal-content'}>
                    <FontBody1 color={FONT_COLOR.NEUTRAL3}>{rmaAction.modalTitleTranslationKey ? getTranslation(rmaAction.modalTitleTranslationKey, scope) : getTranslation(rmaAction.name, scope)}</FontBody1>
                    <FontBody1 color={FONT_COLOR.PRIMARY3}>{getTranslation(rmaAction.descriptionTranslationKey, scope)}</FontBody1>
                    {actionStore.reasonField &&
                        <FormField label={getTranslation(actionStore.reasonField.label, scope)}>
                            <Input value={actionStore.reasonField.value} onChange={actionStore.reasonField.onChange} disabled={isDisabled} />
                        </FormField>
                    }
                    <Checkbox
                        label={getTranslation('SEND_EMAIL_NOTIFICATION', approveRequestScope)}
                        checked={actionStore.sendEmailNotification}
                        onChange={actionStore.setSendEmailNotification}
                        disabled={!actionStore.hasCustomerEmail() || isDisabled}
                    />
                    <ListLayout>
                        {actionStore.sendEmailNotification &&
                            <>
                                <FormField label={getTranslation('ADD_DETAILS_TO_EMAIL', scope)}>
                                    <RichTextEditor maxHeight={200} value={actionStore.emailAdditionalContent} onChange={actionStore.setEmailAdditionalContent} variant={EDITOR_VARIANT.EMAIL_EDITOR_ONLY} disabled={isDisabled} />
                                </FormField>

                                <FormField label={getTranslation('UPLOAD_FILES', approveRequestScope)} instructions={getTranslation('ATTACH_FILES_MAX_SIZE', approveRequestScope, ['9'])}>
                                    <FileUpload fileTypes={['*']} uploadFile={onUploadFile} readType="dataUrl" multiple disabled={isDisabled} buttonText={getTranslation('UPLOAD_FILE', scope)} />
                                </FormField>

                                {(actionStore.emailAttachments.length > 0) && <RmaEmailAttachments actionStore={actionStore} />}
                            </>
                        }
                        {shouldAllowReturnMethodSelect &&
                            <FormField label={getTranslation('RETURN_METHOD', scope)}>
                                <SelectDropdown options={rma.returnMethodOptions} onChange={onReturnMethodChange} value={selectedReturnMethod} disabled={isDisabled} />
                                {!!actionStore.returnMethodWarning
                                    && <Note type={NOTE_TYPE.WARNING} body={getTranslation(actionStore.returnMethodWarning, approveRequestScope)} />}
                            </FormField>
                        }
                    </ListLayout>
                    <div className="action-modal-buttons">
                        <Button text={getTranslation('KEEP_CURRENT_STATUS', scope)} onClick={onCloseClick} variant={BUTTON_VARIANTS.Tertiary} disabled={actionStore.isProcessing || isDisabled} />
                        <Button text={getTranslation(rmaAction.buttonTextTranslationKey, scope)} onClick={onSubmit} variant={BUTTON_VARIANTS.Primary} isLoading={actionStore.isProcessing || isDisabled} hasLoader={true} />
                    </div>
                </div>
            </DefaultModalLayout>
            {(
                actionStore.showReturnMethodChangeWarning &&
                rma.customerCaseStore?.returnLabelFee &&
                rma.rmaReturnMethod
            ) && <RmaActionChangeReturnMethodConfirm
                    actionStore={actionStore}
                    returnLabelFeeAmount={rma.customerCaseStore.returnLabelFee}
                    paymentCurrencyCode={rma.customerCaseStore.paymentCurrencyCode}
                    currentReturnMethodName={rma.returnMethodOptions.find(o => o.value === rma.rmaReturnMethod?.id)?.label}
                    newReturnMethodName={selectedReturnMethod?.label}
                    switchToNoShipmentRequired={!!rma.noShipmentRequiredReturnMethodId && selectedReturnMethod?.value === rma.noShipmentRequiredReturnMethodId}
                />}
        </>
    );
});

const RmaActionModalContentTranslated: React.FC<IProps> = observer(({ rma, rmaAction, onCloseClick }) => {
    const { getTranslation, isLoading } = useTranslation();

    const actionStore = rmaAction.store;

    useEffect(() => {
        const getTranslatedDefaultReason = (): string => {
            if (rmaAction.type === RMA_ACTION_TYPE.CANCEL) {
                return getTranslation('GENERIC_CANCEL_REASON') ?? 'Canceled to your request';
            }
            if (rmaAction.type === RMA_ACTION_TYPE.REJECT) {
                return getTranslation('GENERIC_REJECT_REASON') ?? 'See return policy';
            }
    
            return '';
        }

        /** Set default action reason when needed */
        if (!isLoading) {
            actionStore.reasonField?.onChange(getTranslatedDefaultReason());
        }
    }, [actionStore, rmaAction, getTranslation, isLoading]);

    return (
        <RmaActionModalContent
            rma={rma}
            rmaAction={rmaAction}
            onCloseClick={onCloseClick}
            isDisabled={isLoading}
        />
    );
});

const getTranslationScope = (rmaActionType: RMA_ACTION_TYPE): string|undefined => {
    switch (rmaActionType) {
        case RMA_ACTION_TYPE.CANCEL:
            return 'request-canceled-email';
        case RMA_ACTION_TYPE.REJECT:
            return 'request-rejected-email';
    }
    return undefined;
}

const RmaActionModal: React.FC<IProps> = observer(({ rma, rmaAction, onCloseClick }) => {
    const scope = getTranslationScope(rmaAction.type);
    if (scope) {
        return (
            <TranslationScope scope={scope}>
                <RmaActionModalContentTranslated 
                    rma={rma}
                    rmaAction={rmaAction}
                    onCloseClick={onCloseClick}
                />
            </TranslationScope>
        );
    }
    return <RmaActionModalContent
        rma={rma}
        rmaAction={rmaAction}
        onCloseClick={onCloseClick}
    />
});

export default RmaActionModal;